import Image from "next/image";
import Head from "next/head";
import { useRouter } from "next/router";
import { MdArrowForwardIos } from "react-icons/md";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useMemo } from "react";

export default function Header({ title, image, isRow = false, setAuthDialogOpen }) {
  const router = useRouter();
  const { data: session, status } = useSession();

  const handleGoBack = () => router.push("/");

  const isLawyerIncomplete = useMemo(() => {
    return (
      router.pathname === "/lawyer" &&
      !session?.user?.isCompleteProfile &&
      session?.user?.role === "lawyer"
    );
  }, [router.pathname, session]);

  return (
    <div>
      <Head>
        <link rel="preload" as="image" href="/tqneen/bg-header-slim.svg" />
      </Head>

      <header
        className={clsx(
          "bg-cover bg-center bg-no-repeat relative z-10 px-4 flex",
          // Mobile-optimized heights
          isRow
            ? "h-16 sm:h-20 items-center justify-between flex-row"
            : "h-24 sm:h-28 flex-col justify-center items-center pt-2"
        )}
        style={{ backgroundImage: "url('/tqneen/bg-header-slim.svg')" }}
        role="banner"
      >
        {/* Back Button (only for non-row layout) */}
        {!isRow && (
          <button
            aria-label="Go back"
            onClick={handleGoBack}
            className="absolute right-4 top-4 sm:top-6 text-text-1 size-6 cursor-pointer hover:opacity-80 transition-opacity touch-manipulation"
          >
            <MdArrowForwardIos />
          </button>
        )}

        {/* Brand Logo */}
        <Link href={`/${session?.user?.role}`} className="touch-manipulation">
          <Image
            src="/tqneen/brand.svg"
            alt="Brand Logo"
            width={isRow ? 45 : 50}
            height={isRow ? 45 : 50}
            priority
            className={clsx(
              "transition-transform hover:scale-105",
              { "mb-1 sm:mb-2": !isRow }
            )}
          />
        </Link>

        {/* Title & Notification */}
        <div className="flex flex-col gap-1 sm:gap-2 w-1/2 relative text-center">
          <h3 className="text-text-1 text-base sm:text-lg font-semibold leading-tight">
            {title}
          </h3>

          {isLawyerIncomplete && (
            <p className="absolute -bottom-12 sm:-bottom-16 left-0 right-0 text-red-500 text-sm">
              <span>يرجى استكمال حسابك</span>
              <Link href={`/complete-account?lawyer_id=${session?.user?.id}`}>
                <span className="block text-text-1 font-semibold hover:text-primary-blue-dark transition-all cursor-pointer touch-manipulation">
                  استكمال الحساب
                </span>
              </Link>
            </p>
          )}
        </div>

        {/* Profile Image or Login Button */}
        {isRow && (
          status === "authenticated" ? (
            <Link href="/profile" className="touch-manipulation">
              <Image
                src={image}
                alt="Profile"
                width={40}
                height={40}
                priority
                className="shadow rounded-full object-cover transition-transform hover:scale-105"
              />
            </Link>
          ) : (
            <button
              onClick={() => setAuthDialogOpen(true)}
              className="text-white bg-primary-button hover:bg-primary-button-hover focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg px-4 py-2 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 text-sm"
            >
              تسجيل الدخول
            </button>
          )
        )}
      </header>
    </div>
  );
}

Header.propTypes = {
  title: PropTypes.string.isRequired,
  image: PropTypes.string, // Ensure `image` is a string (URL)
  isRow: PropTypes.bool,
  setAuthDialogOpen: PropTypes.func,
};